import {
  CalendarDate,
  DateValue,
  getDayOfWeek,
  getLocalTimeZone,
  now as idNow,
  today as idToday,
  parseDate,
} from '@internationalized/date';

import { CENTRAL_TIME_ZONE } from '../constants';
import {
  FormatDateOptionsT,
  FormatDateT,
  PaymentDaysValueT,
  PaymentDayValueT,
  PaymentWeekValueT,
} from '../types';

export const sleep = (ms: number): Promise<void> => new Promise((resolve) => { setTimeout(() => resolve(), ms); });
export const now = () => idNow(getLocalTimeZone());
export const today = () => idToday(getLocalTimeZone());
export const yesterday = () => today().add({ days: -1 });
export const tomorrow = () => today().add({ days: 1 });

export const nowCentral = () => idNow(CENTRAL_TIME_ZONE);
export const todayCentral = () => idToday(CENTRAL_TIME_ZONE);
export const tomorrowCentral = () => todayCentral().add({ days: 1 });

export const twoDaysFromTodayCentral = () => todayCentral().add({ days: 2 });

export const lastWeek = () => today().add({ days: -7 });
export const nextWeek = () => today().add({ days: 7 });

export const twoWeeksFromTodayCentral = () => todayCentral().add({ days: 14 });

export const lastMonth = () => today().add({ months: -1 });
export const nextMonth = () => today().add({ months: 1 });

export const isRecentlyVerified = (lastVerifiedAt?: string): boolean => {
  if (!lastVerifiedAt) { return false; }

  try {
    return parseDate(lastVerifiedAt.split('T')[0]).compare(lastMonth()) > 0;
  } catch {
    return false;
  }
};

export const next35Days = () => today().add({ days: 35 });
// This format matches "Monday, August 1, 2025" in Central time
export const dayMonthDateYearFormat: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  timeZone: CENTRAL_TIME_ZONE,
  weekday: 'long',
  year: 'numeric',
};

// This format matches "August 1, 2025" in Central time
export const monthDateYearFormat: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  timeZone: CENTRAL_TIME_ZONE,
  year: 'numeric',
};

// This format matches "Monday, August 1, 2025, 3:00 PM CST"
export const dayMonthDateYearTime12TzFormat: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  month: 'long',
  timeZone: CENTRAL_TIME_ZONE,
  timeZoneName: 'short',
  year: 'numeric',
};

// This format matches "08/01/2025" in Central time
export const mmDdYyyyFormat: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  timeZone: CENTRAL_TIME_ZONE,
  year: 'numeric',
};

const dateFormatter = (options: FormatDateOptionsT): Intl.DateTimeFormatOptions => {
  const {
    numeric,
    withoutWeekday,
    withTime,
  } = options;

  if (numeric) { return mmDdYyyyFormat; }
  if (withTime) { return dayMonthDateYearTime12TzFormat; }
  if (withoutWeekday) { return monthDateYearFormat; }

  return dayMonthDateYearFormat;
};

export const formatDate: FormatDateT = (date, options = {}) => {
  if (!date || !date?.toDate) {
    console.warn('Invalid date passed to formatDate', { date });

    return '';
  }

  // The following use of toDate is safe because all the formatters used specify the timeZone explicitly,
  // so there are no timezone edge cases to worry about.
  return date.toDate(CENTRAL_TIME_ZONE).toLocaleDateString('en-US', dateFormatter(options));
};

export const formatDateTime = (date: DateValue) => formatDate(date, { withTime: true });
export const parseDates = (dateStrings: string[]): DateValue[] => dateStrings.map(parseDate);

export const isFutureDate = (date: DateValue) => date.compare(today()) > 0;
export const isSameDateValue = (date1: DateValue, date2: DateValue) => date1.compare(date2) === 0;

// Message Center timestamps come in the format: `January 1, 2024 8:21 AM CST`
// but are displayed as `January 1, 2024`
export const dateFromMessageTimestamp = (dateStr: string) => dateStr?.split(' ').slice(0, 3).join(' ');

const PAYMENT_DAY_NUMBER: Record<PaymentDayValueT, number> = {
  friday: 5,
  monday: 1,
  thursday: 4,
  tuesday: 2,
  wednesday: 3,
};

export type GetNextPaydaysT = (dayOfWeek: PaymentDayValueT, count?: number) => { label: string; value: string; }[];

export const getNextPaydays: GetNextPaydaysT = (
  dayOfWeek,
  count = 2,
) => {
  const todayDate = todayCentral();
  const targetDay = PAYMENT_DAY_NUMBER[dayOfWeek];
  const currentDay = getDayOfWeek(todayDate, 'en-US');

  const daysUntilNext = (targetDay - currentDay + 7) % 7;

  return Array.from({ length: count }, (_, i) => {
    const nextDate = todayDate.add({ days: daysUntilNext + i * 7 });
    const label = nextDate
      .toDate(CENTRAL_TIME_ZONE) // This use of toDate is safe because the following formatter specifies CT explicitly, so no TZ edge cases.
      .toLocaleDateString('en-US', monthDateYearFormat);

    return { label, value: nextDate.toString() };
  });
};

export const expirationDateString = (isExpired = false) => {
  const nowDate = new Date();
  const year = nowDate.getFullYear();
  const mm = String(nowDate.getMonth() + (isExpired ? 0 : 1)).padStart(2, '0');
  const yy = String(year % 100).padStart(2, '0');

  return `${mm}/${yy}`;
};

export const WEEK_ORDINAL: Record<PaymentWeekValueT, number> = {
  first: 1,
  fourth: 4,
  last: -1,
  second: 2,
  third: 3,
};

export const lastDayOfMonth = (year: number, month: number): number => {
  return new CalendarDate(year, month, 1).add({ months: 1 }).add({ days: -1 }).day;
};

const getCalendarDateInMonth = (year: number, month: number, day: number): CalendarDate => {
  const lastDayInMonth = lastDayOfMonth(year, month);
  const d = day < 0 ? lastDayInMonth : Math.min(day, lastDayInMonth);

  return new CalendarDate(year, month, d);
};

// Returns the Nth occurrence (n = -1 is last) of a weekday (JS day number, Mon = 1)
// within the given year/month.
const getNthWeekdayCalendarDate = (
  year: number,
  month: number,
  jsDayNumber: number,
  n: number,
): CalendarDate => {
  if (n === -1) {
    const lastDay = new CalendarDate(year, month, lastDayOfMonth(year, month));
    const diff = (getDayOfWeek(lastDay, 'en-US') - jsDayNumber + 7) % 7;

    return lastDay.add({ days: -diff });
  }

  const firstDay = new CalendarDate(year, month, 1);
  const diff = (jsDayNumber - getDayOfWeek(firstDay, 'en-US') + 7) % 7;

  return firstDay.add({ days: diff + (n - 1) * 7 });
};

const findNextOccurrence = (
  getCandidates: (year: number, month: number) => CalendarDate[],
): string => {
  const dates = [...getCandidates(today().year, today().month), ...getCandidates(nextMonth().year, nextMonth().month)];

  const next = dates
    .filter((d) => d.compare(today()) >= 0)
    .sort((a, b) => a.compare(b))[0];

  return next.toString();
};

export const getNextPaydateTwiceMonthly = (paymentDays: PaymentDaysValueT): string => {
  const dateParts = paymentDays.split('_and_');
  const date1 = parseInt(dateParts[0], 10);
  const date2 = dateParts[1] === 'last' ? -1 : parseInt(dateParts[1], 10);

  return findNextOccurrence(
    (year, month) => [getCalendarDateInMonth(year, month, date1), getCalendarDateInMonth(year, month, date2)],
  );
};

export const getNextPaydateMonthlySpecificDate = (monthlyPaymentDay: string): string => {
  const day = monthlyPaymentDay === 'last_day' ? -1 : parseInt(monthlyPaymentDay, 10);

  return findNextOccurrence((year, month) => [getCalendarDateInMonth(year, month, day)]);
};

export const getNextPaydateMonthlyRelativeDate = (
  paymentWeek: PaymentWeekValueT,
  day: PaymentDayValueT,
): string => {
  return findNextOccurrence((year, month) => [
    getNthWeekdayCalendarDate(
      year,
      month,
      PAYMENT_DAY_NUMBER[day],
      WEEK_ORDINAL[paymentWeek],
    ),
  ]);
};
